import moment from 'moment';

/**
 * Dates, in one place, because getting them wrong is silent.
 *
 * ── The rule ────────────────────────────────────────────────────────────────
 *
 * Every date in Dalabtire is written by a CLIENT as naive local wall-clock
 * text with no timezone on it, and stored by a server running in UTC. Both
 * clients do it, and have always done it:
 *
 *   phone, orders    `today.toString()`          "2026-09-15 17:53:00.123"
 *   phone, expenses  `date.toIso8601String()`    "2026-09-15T17:53:00.123"
 *   till,  orders    `moment().format(...)`      "2026-09-15 05:53 PM"
 *
 * None of those carry an offset, so `new Date(text)` on a UTC server reads
 * 17:53 as 17:53 UTC and stores an instant three hours before the one the
 * shop actually meant. The value that comes back is therefore NOT a real
 * instant — it is the shop's wall clock wearing a Z.
 *
 * Which gives exactly two rules, and they are the whole of this file:
 *
 *   READING   always `moment.utc(value)` — undoes the server's misread and
 *             returns the time that was meant. Plain `moment(value)` applies
 *             the browser's offset a second time and prints three hours late.
 *
 *   WRITING   always naive local text, never `toISOString()`. A real UTC
 *             instant sent to this server is read as wall clock and comes
 *             back three hours early — the same bug in the other direction.
 *
 * ── Why this file exists ────────────────────────────────────────────────────
 *
 * The rule was already right in three separate places (`formatRowDateTime` on
 * the orders screen, `paperDate.js` for printing, the orders list) and wrong
 * in others, because each screen decided for itself. A screen that gets it
 * wrong does not crash — it quietly shows a time three hours out, which is
 * the kind of thing a shop notices weeks later. So the decision lives here
 * and screens ask rather than choose.
 *
 * ── The one exception ───────────────────────────────────────────────────────
 *
 * `created_at` and `updated_at` are stamped by Mongo, not by a client, so
 * they ARE real instants and must be read with plain `moment()`. The
 * dashboard's ageing figures use them deliberately for that reason. Anything
 * a person typed or a till generated goes through `readDate`.
 */

/**
 * Now, written the way every stored date is written.
 *
 * Not `new Date()`: a real instant would be three hours out of step with the
 * values around it, so a fallback built from one would sort and group wrongly
 * against the rows it sits among.
 */
const nowText = () => moment().format('YYYY-MM-DDTHH:mm:ss.SSS');

/** A value that will parse, so nothing ever renders "Invalid date". */
const safe = (value) =>
  value !== undefined && value !== null && moment(value).isValid() ? value : nowText();

/**
 * A stored date, as the wall clock that was meant.
 *
 * Returns a moment in UTC mode. The instant is unchanged, so sorting,
 * `diff` and `isBefore` behave exactly as before — only what it FORMATS as
 * changes, which is the whole point.
 */
export const readDate = (value) => moment.utc(safe(value));

/**
 * A date on its way to the server.
 *
 * Naive local wall-clock text, in the shape the phone's expenses already
 * send, so a row written here and a row written there are stored identically.
 * Never `toISOString()`.
 *
 * @param {*} value anything moment accepts; omitted means now
 */
export const writeDate = (value) => {
  /**
   * A bare `2026-09-15` is the one input whose meaning is genuinely disputed:
   * the ECMAScript spec says a date-only string is UTC midnight, while moment
   * reads it as LOCAL midnight. Both are defensible and they differ by the
   * offset — which is a whole day when the offset crosses midnight. Rather
   * than depend on which one is in play, a date on its own is spelled out as
   * midnight here.
   */
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return `${value}T00:00:00.000`;
  }

  return moment(safe(value)).format('YYYY-MM-DDTHH:mm:ss.SSS');
};

/** `2026-09-15` — the calendar day a stored date belongs to, in the shop. */
export const dayKeyOf = (value) => readDate(value).format('YYYY-MM-DD');

/**
 * `15 Sep 2026 · 5:53PM`.
 *
 * The format the orders list has always used, kept verbatim so a date reads
 * the same wherever it appears.
 */
export const formatRowDateTime = (value) => readDate(value).format('D MMM YYYY · h:mmA');

/** `15 Sep 2026`. */
export const dayLabel = (value) => readDate(value).format('D MMM YYYY');

/** `5:53 PM`. */
export const timeLabel = (value) => readDate(value).format('h:mm A');

/** `15/Sep/26` — the customer book's column, which is narrow. */
export const shortDayLabel = (value) => readDate(value).format('DD/MMM/YY');

/** `15 Sep 2026` for a plain `YYYY-MM-DD` string that never went to a server. */
export const calendarLabel = (day) => moment(day, 'YYYY-MM-DD').format('D MMM YYYY');
